import {
  PAGINAS_ONBOARDING,
  buildExpenseDraft,
  calcularFaltas,
  dudasDe,
  formatMoneyIn,
  pistaDeArchivo,
  platosDeCarta,
  totalDeFactura,
  type ArchivoSubido,
  type CambiosItemSubido,
  type DailyRevenue,
  type EstadoSubirTodo,
  type ExpenseEntry,
  type ExpenseInput,
  type Ingredient,
  type ItemGasto,
  type ItemPlato,
  type ItemSubido,
  type MenuItem,
  type Recipe,
  type ScannedDocument,
  type TipoDocumento,
  type TrabajoSubirTodo,
} from '@alexiarest/shared';
import type { AlexiaRestApi } from './contract';
import { ApiError } from './error';

/*
 * «Súbelo todo» en la demo: espejo de packages/api/src/routes/subirTodo.ts y
 * subirTodo/procesar.ts, sin S3 ni Alexia. El tipo sale del nombre del
 * archivo (una foto sin pista es una factura), la factura es la de ejemplo y
 * la carta, una de ejemplo. Las reglas (dudas, guardar solo lo claro, no
 * repetir platos, «qué falta») son las de shared, las mismas que la API.
 */

export interface DemoSubirTodoDeps {
  datos(): { revenues: DailyRevenue[]; expenses: ExpenseEntry[]; menuItems: MenuItem[]; recipes: Recipe[]; ingredients: Ingredient[]; suppliers: Array<{ supplierId: string; name: string; taxId?: string }>; country: string; currency: string; tpvConectado: boolean };
  factura: ScannedDocument;
  hoy(): string;
  crearGasto(input: ExpenseInput): Promise<ExpenseEntry>;
  crearPlato(nombre: string, precio: number, seccion?: string): Promise<{ menuItemId: string; recipeId: string }>;
}

const CARTA_DEMO = {
  secciones: [
    { nombre: 'Para compartir', platos: [
      { nombre: 'Croquetas de jamón (8 uds)', precioTexto: '9,50 €', ingredientesMencionados: ['jamón ibérico', 'bechamel'] },
      { nombre: 'Patatas bravas', precioTexto: '6,90 €', ingredientesMencionados: ['patata', 'salsa brava', 'alioli'] },
      { nombre: 'Pulpo a la gallega', precioTexto: 'S/M' },
    ] },
    { nombre: 'Principales', platos: [
      { nombre: 'Arroz meloso de setas', precioTexto: '16,50 €', ingredientesMencionados: ['arroz', 'setas'] },
      { nombre: 'Lubina a la espalda', precioTexto: '19 / 32', descripcion: 'Media / entera' },
    ] },
    { nombre: 'Postres', platos: [{ nombre: 'Tarta de queso', precioTexto: '6,50 €' }] },
  ],
};

export function crearSubirTodoDemo(deps: DemoSubirTodoDeps): Pick<AlexiaRestApi,
  'getSubirTodo' | 'empezarSubirTodo' | 'getSubirTodoJob' | 'subirArchivoSubirTodo' | 'decirTipoSubirTodo' | 'reintentarSubirTodo' | 'editarItemSubirTodo' | 'guardarSubirTodo'> {
  let job: TrabajoSubirTodo | null = null;
  let archivos: ArchivoSubido[] = [];
  let items: ItemSubido[] = [];
  let paginas = 0;
  let siguiente = 0;

  const requireJob = (jobId: string) => {
    if (!job || job.jobId !== jobId) throw new ApiError('NOT_FOUND', 'No encuentro esa subida. Empieza otra.', 404);
    return job;
  };

  const estado = (): EstadoSubirTodo => {
    const d = deps.datos();
    return {
      job: job!,
      archivos: [...archivos],
      items: items.map((item) => ({ ...item, dudas: item.estado === 'propuesto' ? dudasDe(item) : [] })),
      paginas: { usadas: paginas, limite: PAGINAS_ONBOARDING },
      faltas: calcularFaltas({ revenues: d.revenues, expenses: d.expenses, menuItems: d.menuItems, recipes: d.recipes, ingredients: d.ingredients, items, tpvConectado: d.tpvConectado }),
    };
  };

  const leer = (archivo: ArchivoSubido) => {
    const now = new Date().toISOString();
    const tipo: TipoDocumento = archivo.tipoDicho ?? pistaDeArchivo(archivo.nombre, archivo.contentType) ?? 'factura';
    items = items.filter((i) => i.fileId !== archivo.fileId || i.estado === 'guardado');
    archivo.tipo = tipo;
    archivo.tipoConfianza = 'alta';
    archivo.actualizadoAt = now;
    const d = deps.datos();
    if (tipo === 'carta') {
      const enCarta = new Map(d.menuItems.filter((m) => m.active).map((m) => [m.name.toLowerCase(), m]));
      const platos = platosDeCarta([CARTA_DEMO]);
      items.push(...platos.map((plato, index): ItemPlato => {
        const ya = enCarta.get(plato.nombre.valor.toLowerCase());
        return { kind: 'plato', jobId: archivo.jobId, itemId: `${archivo.fileId}-${index}`, fileId: archivo.fileId, estado: 'propuesto', ...plato, ...(ya ? { yaEnCarta: { menuItemId: ya.menuItemId, precio: ya.sellingPriceGross } } : {}), actualizadoAt: now };
      }));
      archivo.estado = 'listo';
      archivo.resumen = `${platos.length} platos`;
      return;
    }
    if (!['factura', 'albaran', 'nomina'].includes(tipo)) {
      archivo.estado = 'no_se_lee';
      archivo.resumen = tipo === 'escandallo' ? 'Es un escandallo. De momento súbelo en Excel desde «Importar recetas».' : 'Son ventas. Súbelas con «Importar ventas» o conecta tu TPV.';
      return;
    }
    if (paginas >= PAGINAS_ONBOARDING) {
      archivo.estado = 'error';
      archivo.error = `Ya he leído las ${PAGINAS_ONBOARDING} páginas de facturas de tu puesta en marcha.`;
      return;
    }
    paginas += 1;
    const draft = buildExpenseDraft(deps.factura, { suppliers: d.suppliers, country: d.country, recientes: d.expenses.slice(-200), documentType: tipo === 'albaran' ? 'albaran' : 'factura', today: deps.hoy() });
    const propuesta = tipo === 'nomina' ? { ...draft, category: 'sueldos_salarios' as const, nature: 'fijo' as const } : draft;
    const item: ItemGasto = { kind: 'gasto', jobId: archivo.jobId, itemId: `${archivo.fileId}-0`, fileId: archivo.fileId, estado: 'propuesto', documentType: tipo === 'albaran' ? 'albaran' : 'factura', propuesta, actualizadoAt: now };
    // Como la API: la misma factura dos veces en la tanda se marca.
    const repetida = items.some((i) => i.kind === 'gasto' && i.estado !== 'descartado' && i.propuesta.invoiceNumber && i.propuesta.invoiceNumber === propuesta.invoiceNumber);
    if (repetida) item.propuesta = { ...item.propuesta, duplicado: { certeza: 'segura', motivo: 'Misma factura en esta subida' } as never };
    items.push(item);
    archivo.estado = 'listo';
    archivo.resumen = `${propuesta.supplierName ?? propuesta.concept} · ${formatMoneyIn(totalDeFactura(propuesta), d.currency)}`;
  };

  return {
    async getSubirTodo() {
      return job ? estado() : { job: null };
    },
    async empezarSubirTodo() {
      const now = new Date().toISOString();
      job = { jobId: `demo-${Date.now().toString(36)}`, tenantId: 'demo', creadoPor: 'demo', creadoAt: now, actualizadoAt: now, paginasLeidas: 0, costeIaEur: 0 };
      archivos = [];
      items = [];
      return estado();
    },
    async getSubirTodoJob(jobId) {
      requireJob(jobId);
      return estado();
    },
    async subirArchivoSubirTodo(jobId, file, nombre, opciones = {}) {
      requireJob(jobId);
      const now = new Date().toISOString();
      const archivo: ArchivoSubido = {
        jobId, fileId: `f${(siguiente += 1)}`, nombre, contentType: file.type || 'application/pdf', key: `demo/${nombre}`,
        ...(opciones.locationId ? { locationId: opciones.locationId } : {}), estado: 'leyendo', intentos: 1, creadoAt: now, actualizadoAt: now,
      };
      archivos.push(archivo);
      // Un respiro para que se vea «Alexia está leyendo…», como con la cola de verdad.
      setTimeout(() => leer(archivo), 1200);
      return { ...archivo };
    },
    async decirTipoSubirTodo(jobId, fileId, tipo) {
      requireJob(jobId);
      const archivo = archivos.find((a) => a.fileId === fileId);
      if (!archivo) throw new ApiError('NOT_FOUND', 'Ese archivo ya no está en la subida.', 404);
      archivo.tipoDicho = tipo;
      leer(archivo);
      return { ...archivo };
    },
    async reintentarSubirTodo(jobId, fileId) {
      requireJob(jobId);
      const archivo = archivos.find((a) => a.fileId === fileId);
      if (!archivo) throw new ApiError('NOT_FOUND', 'Ese archivo ya no está en la subida.', 404);
      delete archivo.error;
      leer(archivo);
      return { ...archivo };
    },
    async editarItemSubirTodo(jobId, itemId, cambios: CambiosItemSubido) {
      requireJob(jobId);
      const i = items.findIndex((x) => x.itemId === itemId);
      if (i < 0) throw new ApiError('NOT_FOUND', 'Eso ya no está en la subida.', 404);
      let item = items[i];
      if (item.estado === 'guardado') throw new ApiError('CONFLICT', 'Ya está guardado: cámbialo desde su pantalla.', 409);
      if (cambios.descartar) item = { ...item, estado: 'descartado' };
      if (cambios.recuperar) item = { ...item, estado: 'propuesto' };
      if (item.kind === 'gasto' && (cambios.gasto || cambios.confirmar)) {
        const { noEsRepetida, documentType, ...campos } = cambios.gasto ?? {};
        const propuesta = { ...item.propuesta, ...Object.fromEntries(Object.entries(campos).filter(([, v]) => v !== undefined)) };
        if (campos.supplierName) { propuesta.supplierId = undefined; propuesta.concept = campos.concept ?? campos.supplierName; }
        item = { ...item, ...(documentType ? { documentType } : {}), propuesta: { ...propuesta, confianza: { proveedor: 'alta', fecha: 'alta', importe: 'alta', categoria: 'alta' }, avisos: [], ...(noEsRepetida || !propuesta.duplicado ? { duplicado: undefined } : {}) } };
      }
      if (item.kind === 'plato' && (cambios.plato || cambios.confirmar)) {
        const precio = cambios.plato?.precio ?? item.precio.valor;
        item = { ...item, nombre: { valor: cambios.plato?.nombre ?? item.nombre.valor, confianza: 'alta' }, precio: precio == null ? item.precio : { valor: precio, confianza: 'alta' }, ...(cambios.plato?.seccion !== undefined ? { seccion: cambios.plato.seccion || undefined } : {}) };
      }
      items[i] = item;
      return { ...item, dudas: item.estado === 'propuesto' ? dudasDe(item) : [] };
    },
    async guardarSubirTodo(jobId, grupo, itemIds) {
      requireJob(jobId);
      const kind = grupo === 'gastos' ? 'gasto' : 'plato';
      const candidatos = items.filter((item) => item.kind === kind && item.estado === 'propuesto' && (!itemIds || itemIds.includes(item.itemId)));
      const listos = candidatos.filter((item) => dudasDe(item).length === 0);
      const errores: Array<{ itemId: string; error: string }> = [];
      let guardados = 0;
      for (const item of listos) {
        try {
          let guardadoComo: ItemSubido['guardadoComo'];
          if (item.kind === 'gasto') {
            const gasto = await deps.crearGasto({ ...item.propuesta, notes: item.propuesta.vatNote, documentType: item.documentType } as unknown as ExpenseInput);
            guardadoComo = { expenseId: gasto.entryId };
          } else {
            const ya = deps.datos().menuItems.find((m) => m.active && m.name.toLowerCase() === item.nombre.valor.toLowerCase());
            guardadoComo = ya ? { menuItemId: ya.menuItemId, recipeId: ya.recipeId } : await deps.crearPlato(item.nombre.valor, item.precio.valor!, item.seccion);
          }
          items = items.map((x) => (x.itemId === item.itemId ? { ...x, estado: 'guardado', guardadoComo } : x));
          guardados += 1;
        } catch (error) {
          errores.push({ itemId: item.itemId, error: error instanceof Error ? error.message : 'No se ha podido guardar.' });
        }
      }
      return { guardados, conDudas: candidatos.length - listos.length, errores, estado: estado() };
    },
  };
}
